/**
 * reach.js — Site-aware reach calculation for PlanIQ.
 *
 * External platforms (site_id null) use the legacy 3-pool Sainsbury path unchanged.
 * Site-bound platforms (site_id set) use a 4-tier hierarchy:
 *   Tier 1: Sainsbury within (site, overlap_group) → cap at site_device_users × audComp
 *   Tier 2: Combine overlap_groups within a site → cap at site_total_users × audComp
 *   Tier 3: Combine sites → modified Sainsbury with site_overlap coefficients + sanity clamp
 *   Tier 4: Combine publisher reach with external platform reach
 *
 * Per-platform conversion (identical for site-bound and external):
 *   cookies      = viewable weekly cookies (computed upstream in calcFlight)
 *   real_users   = cookies / ruc
 *   ta_real_users = real_users × audComp
 *   reach_frac   = ta_real_users / universe
 */

// Default frequency cap when platform.max_weekly_frequency is null.
export const MAX_FREQ_DEFAULT = 10;

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Standard Sainsbury combination (independence assumption). */
export function sb2(a, b) { return a + b - a * b; }

/**
 * Modified Sainsbury with overlap coefficient.
 * oc=0 → independence (standard Sainsbury).
 * oc=1 → near-total overlap (combined ≈ max(a,b) for moderate values).
 * a and b are reach fractions [0,1]; result is clamped to [0,1].
 */
export function sbOverlap(a, b, oc) {
  return Math.max(0, Math.min(1, a + b - a * b * (1 + oc)));
}

/** Sainsbury combination of an array of reach fractions. */
export function sbN(arr) { return arr.reduce((acc, r) => sb2(acc, r), 0); }

/**
 * Legacy 3-pool Sainsbury reach for external platforms — unchanged.
 * entries: [{ cookies, ruc, audComp }]
 * Returns reach fraction [0, 0.98].
 */
export function poolReach(entries, universe) {
  if (!entries.length || !universe) return 0;
  const reaches = entries.map(e => {
    const ru = (e.cookies || 0) / (e.ruc || 1) * (e.audComp || 0.65);
    return Math.min(0.999, ru / universe);
  });
  return reaches.reduce((acc, r) => acc + r - acc * r, 0);
}

// ── Cross-device overlap coefficient ─────────────────────────────────────────

/**
 * Derive cross-device overlap coefficient from Gemius device counts.
 * Formula: (desktop + mobile + app - total) / min(desktop, mobile)
 * Clamped to [0, 1].
 */
export function deriveCrossDeviceOc(total, desktop, mobile, app) {
  const d = desktop || 0;
  const m = mobile || 0;
  const a = app || 0;
  const minDM = Math.min(d || Infinity, m || Infinity);
  if (!d || !m || !total || minDM === Infinity) return 0.40;
  const oc = (d + m + a - total) / minDM;
  return Math.max(0, Math.min(1, oc));
}

// ── Tier 2 overlap coefficients ───────────────────────────────────────────────

const WITHIN_GROUP_OC     = 0.90; // same site, same device, same format context
const DESKTOP_TAKEOVER_OC = 0.50;
const DEVICE_APP_OC       = 0.40;
const MOBILE_APP_OC       = 0.40;

/**
 * Returns the Tier 2 overlap coefficient between two overlap_groups on the same site.
 * site: { monthly_real_users_total, monthly_real_users_desktop,
 *         monthly_real_users_mobile, monthly_real_users_app }
 */
export function tier2GroupOc(groupA, groupB, site) {
  if (groupA === groupB) return WITHIN_GROUP_OC;
  const a = [groupA, groupB].sort().join('|');
  if (a === 'desktop_banners|desktop_takeover') return DESKTOP_TAKEOVER_OC;
  if ((groupA.startsWith('desktop') && groupB === 'mobile') ||
      (groupB.startsWith('desktop') && groupA === 'mobile')) {
    return deriveCrossDeviceOc(
      site.monthly_real_users_total,
      site.monthly_real_users_desktop,
      site.monthly_real_users_mobile,
      site.monthly_real_users_app || 0,
    );
  }
  if ((groupA.startsWith('desktop') && groupB === 'app') ||
      (groupB.startsWith('desktop') && groupA === 'app')) return DEVICE_APP_OC;
  if (a === 'app|mobile') return MOBILE_APP_OC;
  return 0.40;
}

// ── Main entry-point ─────────────────────────────────────────────────────────

/**
 * Calculate combined weekly reach fraction for a set of platform entries.
 *
 * @param {Object} params
 * @param {Array}  params.entries    - Per-platform data:
 *                   { platformId, siteId, device, overlapGroup, reach_pool,
 *                     cookies,    // viewable weekly cookies (impr/nWeeks/frq × viewability)
 *                     ruc,        // real_users_coef — applied for ALL platforms
 *                     audComp,    // audience composition fraction
 *                     viewability }
 * @param {Object} params.sitesMap   - { [siteId]: site row } or {}
 * @param {Array}  params.siteOverlaps - [{ site_a_id, site_b_id, overlap_coefficient }]
 * @param {number} params.universe   - Target audience size (integer)
 * @returns {number} Reach fraction in [0, 0.98]
 */
export function calculatePlanReach({ entries, sitesMap, siteOverlaps, universe }) {
  if (!entries || !entries.length || !universe) return 0;
  sitesMap = sitesMap || {};
  siteOverlaps = siteOverlaps || [];

  // Split: site-bound (have a site entry in sitesMap) vs external
  const siteEntries     = entries.filter(e => e.siteId && sitesMap[e.siteId]);
  const externalEntries = entries.filter(e => !e.siteId || !sitesMap[e.siteId]);

  // ── External: legacy 3-pool Sainsbury (unchanged) ────────────────────────
  const extPools = { yt: [], social: [], other: [] };
  externalEntries.forEach(e => {
    const pool = e.reach_pool === 'yt' ? 'yt' : e.reach_pool === 'social' ? 'social' : 'other';
    extPools[pool].push({ cookies: e.cookies, ruc: e.ruc, audComp: e.audComp });
  });
  const rYT = poolReach(extPools.yt, universe);
  const rSoc = poolReach(extPools.social, universe);
  const rOth = poolReach(extPools.other, universe);
  const externalReach = Math.min(0.98, 1 - (1 - rYT) * (1 - rSoc) * (1 - rOth));

  if (!siteEntries.length) return {
    combined: Math.min(0.98, externalReach),
    publisher: 0,
    external: externalReach,
    publisherCap: null,
  };

  // ── Tier 1: Sainsbury within (site, overlap_group) ───────────────────────
  const groupMap = {};
  siteEntries.forEach(e => {
    const key = `${e.siteId}|${e.overlapGroup || 'default'}`;
    if (!groupMap[key]) groupMap[key] = { siteId: e.siteId, overlapGroup: e.overlapGroup || 'default', entries: [] };
    groupMap[key].entries.push(e);
  });

  const groupResults = {}; // key → { siteId, overlapGroup, frac, avgAudComp }
  Object.values(groupMap).forEach(g => {
    const site = sitesMap[g.siteId];
    const ents = g.entries;

    // Per-platform TA real users: cookies / ruc × audComp
    // This is identical to the formula used in the external poolReach path.
    const platTARU = ents.map(e => (e.cookies || 0) / (e.ruc || 1) * (e.audComp || 0.65));

    // Sainsbury within group (oc=0.90: same site, same device, same context)
    let combinedFrac = 0;
    platTARU.forEach(taru => {
      const frac = Math.min(0.999, taru / universe);
      combinedFrac = sbOverlap(combinedFrac, frac, WITHIN_GROUP_OC);
    });

    // Device cap: site_device_real_users × avgAudComp / universe
    // Cap applied AFTER audience filter (spec requirement).
    const device = ents[0].device || 'desktop';
    const rawDevicePop = device === 'mobile' ? site.monthly_real_users_mobile
                       : device === 'app'    ? site.monthly_real_users_app
                       :                       site.monthly_real_users_desktop;
    const totalCookies = ents.reduce((s, e) => s + (e.cookies || 0), 0) || 1;
    const avgAudComp = ents.reduce((s, e) => s + (e.audComp || 0.65) * (e.cookies || 0), 0) / totalCookies;
    const deviceCapFrac = rawDevicePop ? (rawDevicePop * avgAudComp) / universe : null;

    const tier1Frac = deviceCapFrac ? Math.min(combinedFrac, deviceCapFrac) : combinedFrac;

    groupResults[`${g.siteId}|${g.overlapGroup || 'default'}`] = {
      siteId: g.siteId,
      overlapGroup: g.overlapGroup || 'default',
      frac: tier1Frac,
      avgAudComp,
    };
  });

  // ── Tier 2: Sainsbury across overlap_groups within each site ─────────────
  const siteIds = [...new Set(siteEntries.map(e => e.siteId))];
  const tier2Results = {};
  const siteAvgAudCompMap = {}; // siteId → audComp (for Tier 3 sanity clamp)

  siteIds.forEach(siteId => {
    const site = sitesMap[siteId];
    const siteGroups = Object.values(groupResults).filter(g => g.siteId === siteId);

    // Site-level avgAudComp (weighted by cookies) — stored for Tier 3 clamp
    const allCookies = siteEntries.filter(e => e.siteId === siteId)
                                   .reduce((s, e) => s + (e.cookies || 0), 0) || 1;
    const siteAvgAudComp = siteEntries.filter(e => e.siteId === siteId)
                                       .reduce((s, e) => s + (e.audComp || 0.65) * (e.cookies || 0), 0) / allCookies;
    siteAvgAudCompMap[siteId] = siteAvgAudComp;

    // Site cap: site_total_real_users × avgAudComp / universe (no viewability — already in cookies)
    const siteCap = (site.monthly_real_users_total * siteAvgAudComp) / universe;

    if (siteGroups.length === 1) {
      tier2Results[siteId] = Math.min(siteGroups[0].frac, siteCap);
      return;
    }

    // Pairwise combine groups with Tier 2 overlap coefficients
    let combined = 0;
    siteGroups.forEach(g => {
      if (combined === 0) { combined = g.frac; return; }
      const oc = tier2GroupOc(g.overlapGroup, siteGroups[0].overlapGroup, site);
      combined = sbOverlap(combined, g.frac, oc);
    });

    tier2Results[siteId] = Math.min(combined, siteCap, 0.98);
  });

  // ── Tier 3: Sainsbury across sites with overlap coefficients ─────────────
  const overlapLookup = {};
  siteOverlaps.forEach(o => {
    const a = Math.min(o.site_a_id, o.site_b_id);
    const b = Math.max(o.site_a_id, o.site_b_id);
    overlapLookup[`${a}|${b}`] = o.overlap_coefficient;
  });
  function getSiteOc(idA, idB) {
    const a = Math.min(idA, idB), b = Math.max(idA, idB);
    const v = overlapLookup[`${a}|${b}`];
    return v !== undefined ? v : 0.40;
  }

  let publisherReach = 0;
  const siteIdList = Object.keys(tier2Results).map(Number);
  siteIdList.forEach(siteId => {
    const r = tier2Results[siteId];
    if (publisherReach === 0) { publisherReach = r; return; }
    const oc = getSiteOc(siteId, siteIdList[0]);
    publisherReach = sbOverlap(publisherReach, r, oc);
  });

  // Sanity clamp: publisher reach ≤ sum(selected site total TA users) / universe
  // Sainsbury math should never exceed this, but floating-point and rounding can drift.
  const publisherRUCap = siteIdList.reduce((s, sid) => {
    const site = sitesMap[sid];
    if (!site) return s;
    return s + site.monthly_real_users_total * (siteAvgAudCompMap[sid] || 0.65);
  }, 0);
  publisherReach = Math.min(publisherReach, publisherRUCap / universe);

  // ── Tier 4: combined result with split components for multi-week accumulation ─
  const combined = Math.min(0.98, sb2(publisherReach, externalReach));
  return {
    combined,
    publisher: publisherReach,
    external: externalReach,
    publisherCap: Math.min(1, publisherRUCap / universe),
  };
}

// ── Inventory frequency cap helper ───────────────────────────────────────────

/**
 * Compute effective weekly frequency for a site-bound platform.
 * Uses the same per-platform TA real user logic as Tier 1 of calculatePlanReach.
 * Returns null for external platforms (site null) — caller must skip the warning.
 *
 * @param {Object} params
 * @param {number} params.cookies   Weekly viewable cookies (impr/nWeeks/frq × viewability)
 * @param {number} params.frq       Target weekly frequency (to recover weekly viewable impressions)
 * @param {number} params.ruc       real_users_coef
 * @param {number} params.audComp   Audience composition fraction [0,1]
 * @param {string} params.device    'mobile' | 'desktop' | 'app'
 * @param {Object|null} params.site Site row from sitesMap, or null for external platforms.
 *                                  Must use long keys: monthly_real_users_desktop/mobile/app.
 * @returns {number|null} Effective viewable exposures per TA user per week, or null.
 */
export function computeEffectiveFrequency({ cookies, frq, ruc, audComp, device, site }) {
  if (!site) return null;
  const weeklyViewableImpr = (cookies || 0) * (frq || 1);
  const taRealUsers = (cookies || 0) / (ruc || 1) * (audComp || 0.65);
  const rawDevicePop = device === 'mobile' ? site.monthly_real_users_mobile
                     : device === 'app'    ? site.monthly_real_users_app
                     :                       site.monthly_real_users_desktop;
  const deviceCapUsers = rawDevicePop ? rawDevicePop * (audComp || 0.65) : null;
  const cappedUsers = deviceCapUsers ? Math.min(taRealUsers, deviceCapUsers) : taRealUsers;
  if (cappedUsers <= 0) return null;
  return weeklyViewableImpr / cappedUsers;
}
